import { useState } from 'react';
import { Camera, Check, Pause, Pencil, Play, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { TrackerUser } from '@pms/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDeleteTrackerUser } from '@/hooks/tracking/useDeleteTrackerUser';
import { useUpdateTrackerUser } from '@/hooks/tracking/useUpdateTrackerUser';
import { formatDateTimeIST, formatRelativeTime } from '@/lib/dateFormat';
import { formatCount, INTERVAL_CHOICES, intervalLabel } from './trackerConstants';

/** The tracker returns everyone at once, so the table pages on the client. */
const PAGE_SIZE = 20;

interface Props {
  users: TrackerUser[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  defaultIntervalSecs: number;
  canManage: boolean;
  onOpenScreenshots: (userId: string) => void;
}

function StatusBadge({ user }: { user: TrackerUser }) {
  if (!user.enabled) return <Badge variant="warning">Paused</Badge>;
  if (user.online) return <Badge variant="success">Working</Badge>;
  return <Badge variant="secondary">Offline</Badge>;
}

/** Inline rename — only the display name changes, never `userId` (it names the R2 folder). */
function NameCell({ user, canManage }: { user: TrackerUser; canManage: boolean }) {
  const update = useUpdateTrackerUser();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.name);

  const save = () => {
    const name = draft.trim();
    if (!name) {
      toast.error('Name cannot be empty');
      return;
    }
    if (name === user.name) {
      setEditing(false);
      return;
    }
    update.mutate(
      { userId: user.userId, patch: { name } },
      {
        onSuccess: () => {
          toast.success('Name updated');
          setEditing(false);
        },
      },
    );
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          value={draft}
          maxLength={120}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="h-8 min-w-[140px]"
          aria-label="Name"
        />
        <Button size="icon" variant="ghost" onClick={save} disabled={update.isPending} aria-label="Save name">
          <Check className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" onClick={() => setEditing(false)} aria-label="Cancel">
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="font-medium">{user.name || user.userId}</span>
      {canManage && (
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 sm:h-7 sm:w-7"
          onClick={() => {
            setDraft(user.name);
            setEditing(true);
          }}
          aria-label="Rename"
          title="Rename"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export function TrackerPeople({ users, loading, error, onRetry, defaultIntervalSecs, canManage, onOpenScreenshots }: Props) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [removing, setRemoving] = useState<TrackerUser | null>(null);
  const update = useUpdateTrackerUser();
  const remove = useDeleteTrackerUser();

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? users.filter((u) => `${u.name} ${u.email} ${u.userId} ${u.machine ?? ''}`.toLowerCase().includes(needle))
    : users;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeInterval = (user: TrackerUser, secs: number) =>
    update.mutate(
      { userId: user.userId, patch: { intervalSecs: secs } },
      { onSuccess: () => toast.success(`${user.name || user.userId}: captures every ${intervalLabel(secs)}`) },
    );

  const togglePause = (user: TrackerUser) =>
    update.mutate(
      { userId: user.userId, patch: { enabled: !user.enabled } },
      { onSuccess: () => toast.success(user.enabled ? 'Tracking paused' : 'Tracking resumed') },
    );

  const columns: Column<TrackerUser>[] = [
    {
      key: 'person',
      header: 'Person',
      cell: (u) => (
        <div>
          <NameCell key={u.name} user={u} canManage={canManage} />
          <p className="text-xs text-muted-foreground">
            {u.email}
            {u.email && ' · '}
            {u.machine ?? 'Unknown machine'}
          </p>
        </div>
      ),
    },
    { key: 'status', header: 'Status', cell: (u) => <StatusBadge user={u} /> },
    { key: 'today', header: 'Today', className: 'text-right tabular-nums', cell: (u) => formatCount(u.capturesToday) },
    {
      key: 'total',
      header: 'All time',
      className: 'hidden text-right tabular-nums sm:table-cell',
      cell: (u) => formatCount(u.totalCaptures),
    },
    {
      key: 'last',
      header: 'Last capture',
      className: 'hidden md:table-cell',
      cell: (u) => (
        <span className="text-xs" title={formatDateTimeIST(u.lastCaptureAt)}>
          {formatRelativeTime(u.lastCaptureAt) || 'Never'}
        </span>
      ),
    },
    {
      key: 'interval',
      header: 'Interval',
      cell: (u) => {
        const interval = u.intervalSecs ?? defaultIntervalSecs;
        const choices = INTERVAL_CHOICES.includes(interval)
          ? INTERVAL_CHOICES
          : [...INTERVAL_CHOICES, interval].sort((a, b) => a - b);
        return (
          <Select
            value={String(interval)}
            onValueChange={(v) => changeInterval(u, Number(v))}
            disabled={!canManage || update.isPending}
          >
            <SelectTrigger className="h-8 w-[120px] text-xs sm:h-8" aria-label="Capture interval">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {choices.map((secs) => (
                <SelectItem key={secs} value={String(secs)}>
                  {intervalLabel(secs)}
                  {u.intervalSecs === null && secs === defaultIntervalSecs ? ' (default)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (u) => (
        <div className="flex items-center justify-end gap-1">
          {canManage && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => togglePause(u)}
              disabled={update.isPending}
              aria-label={u.enabled ? 'Pause tracking' : 'Resume tracking'}
              title={u.enabled ? 'Pause' : 'Resume'}
            >
              {u.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onOpenScreenshots(u.userId)}
            aria-label={`Screenshots of ${u.name || u.userId}`}
            title="Screenshots"
          >
            <Camera className="h-4 w-4" />
          </Button>
          {canManage && (
            <Button
              size="icon"
              variant="ghost"
              className="hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setRemoving(u)}
              aria-label={`Remove ${u.name || u.userId}`}
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={q}
          onChange={(v) => { setQ(v); setPage(1); }}
          placeholder="Search name, email or machine"
          containerClassName="w-full sm:max-w-sm"
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(u) => u.userId}
        loading={loading && users.length === 0}
        loadingRows={5}
        error={error}
        onRetry={onRetry}
        empty={users.length === 0
          ? 'No one has connected yet. A person appears here the first time their tracker app starts.'
          : 'No one matches this search.'}
        pagination={{ page: currentPage, totalPages, total: filtered.length, onPageChange: setPage }}
      />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => { if (!open) setRemoving(null); }}
        title={`Remove ${removing?.name || removing?.userId || ''}?`}
        description="Their tracker records (captures and screenshot rows) are removed from the tracker database. The files in the R2 bucket are not deleted. If their tracker app runs again, they will reappear here."
        confirmLabel="Remove"
        onConfirm={() => { if (removing) remove.mutate(removing.userId); }}
      />
    </div>
  );
}
